import styled, { x } from '@xstyled/styled-components';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { animated, config, useTransition } from '@react-spring/web';
import { shallow } from 'zustand/shallow';
import { useSequenceDetailsQuery } from '~/data';
import { select } from '~/lib/select';
import { shakeVerticalAnimation } from '~/ui/animation';
import { NotFound } from '~/ui/not-found/NotFound';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import {
  Sequence as SequenceDef,
  TrainingState,
  useTrainingStore
} from '../state';
import { useTrainingParams } from '../useTrainingParams';
import { ComboButton } from './ComboButton';
import { useSequenceStore } from './SequenceStore';
import { useLmsAdapter } from '~/lms';
import { translate } from './templates';
import { Root } from './templates/items/root/Root';
import { usePrefetchSequence } from './usePrefetchSequence';
import ScrollDownLightIcon from '~/assets/icons/scroll-down-light.svg';
import ScrollDownDarkIcon from '~/assets/icons/scroll-down-dark.svg';
import { SkipButtons } from '~/training/sequence/SkipButtons';
import { TenSecsBack } from '~/ui/video-player/TenSecsBack';
import { InternetOff } from './InternetOff';
import { ReplayMediaBtn } from '~/ui/video-player/ReplayMediaBtn';
import {
  useContentReady,
  shouldEnableContentReadyTracking
} from '~/lib/useContentReady';
import useIsDarkMode from '~/lib/themes/hooks/useIsDarkMode';
import { t } from 'ttag';

/**
 * Sequence components will be placed on the same grid cell so they can overlap
 * for the transition animation
 */
const SequenceWrapper = animated(styled.div<{ setBackgroundColor: boolean }>`
  grid-row: 1;
  grid-column: 1;
  background-color: ${p =>
    p.setBackgroundColor ? p.theme.colors.pinkLight : 'transparent'};
`);

const selector = (s: TrainingState) =>
  select(
    s,
    'activateSequence',
    'items',
    'companyName',
    'logoUrl',
    'featureShowLlama',
    'scorePercent'
  );

const mediaSelector = (s: MediaPlayerStore) => select(s, 'reset');

/** CAT-1281: ignore tiny overflow (subpixel / LMS iframe chrome) so Next
 *  is not blocked when the learner cannot meaningfully scroll. */
const SCROLL_OVERFLOW_THRESHOLD_PX = 30;

interface SequenceComponentProps {
  sequence: SequenceDef;
}

/**
 * Renders the right sequence component based on the type
 */
const SequenceComponent = ({ sequence }: SequenceComponentProps) => {
  const setAutoContinue = useSequenceStore(s => s.setAutoContinue);
  const setPersonalization = useSequenceStore(s => s.setPersonalization);
  const companyName = useTrainingStore(s => s.companyName);
  const logoUrl = useTrainingStore(s => s.logoUrl);
  const featureShowLlama = useTrainingStore(s => s.featureShowLlama);

  const { learnerTrainingId } = useTrainingParams();
  const { data } = useSequenceDetailsQuery({
    variables: {
      learnerTrainingUuid: learnerTrainingId!,
      chapterSequenceId: sequence.chapterSequenceId
    }
  });

  const RootComponent = useMemo(() => {
    return (
      data?.sequence &&
      new Root(
        translate({
          ...data.sequence,
          companyName: companyName,
          logoUrl: logoUrl,
          featureShowLlama: featureShowLlama
        })
      ).component()
    );
  }, [data]);

  useEffect(() => {
    if (!data) return;

    // TODO: change setPersonalization logic
    setPersonalization(data?.sequence?.personalization || false);

    if (data?.sequence?.templateName?.substring(0, 4) === 'Quiz') {
      setAutoContinue(false);
    } else {
      // setAutoContinue(data.sequence.autoContinue || false);
      setAutoContinue(false);
    }
  }, [data]);

  return RootComponent ? <RootComponent key={sequence.id} /> : null;
};

/**
 * Figma EDS-CAT 119:3948 (light) / 119:3958 (dark).
 * Fixed above captions (bottom 52px). Asset points left; -90deg faces down.
 */
const ScrollIndicator = styled.div`
  position: fixed;
  left: 50%;
  bottom: 52px;
  transform: translateX(-50%);
  z-index: 100;
  pointer-events: none;
  width: 35px;
  height: 35px;
`;

const ScrollIndicatorMotion = styled.div`
  width: 35px;
  height: 35px;
  animation: ${shakeVerticalAnimation} 8s ease infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const scrollDownIconCss = `
  width: 35px;
  height: 35px;
  display: block;
  /* Figma asset faces left; rotate to point down */
  transform: rotate(-90deg);
`;

const ScrollDownLight = styled(ScrollDownLightIcon)`
  ${scrollDownIconCss}
`;

const ScrollDownDark = styled(ScrollDownDarkIcon)`
  ${scrollDownIconCss}
`;

export const Sequence = () => {
  const { reset } = useMediaPlayerStore(mediaSelector, shallow);
  const { sequenceId } = useTrainingParams();
  const { activateSequence, items, scorePercent } = useTrainingStore(
    selector,
    shallow
  );

  const newChapter = usePrefetchSequence(sequenceId!);
  const [canScroll, setCanScroll] = useState(false);
  /** True from slide change until overflow is measured — holds Next without
   *  flashing the scroll hint / announcing on non-scrollable slides. */
  const [scrollCheckPending, setScrollCheckPending] = useState(false);
  const containerRef = useRef<HTMLElement>();
  const childRef = useRef<HTMLElement>();
  const setRxdInteractions = useSequenceStore(s => s.setRxdInteractions);
  const clearBlocker = useSequenceStore(s => s.clearBlocker);
  const setNeedsScrollDown = useSequenceStore(s => s.setNeedsScrollDown);
  const lms = useLmsAdapter();
  const { setPlayer } = useGlobalMediaStore();
  const isDarkMode = useIsDarkMode();

  const { learnerTrainingId } = useTrainingParams();

  const shouldTrackContentReady = shouldEnableContentReadyTracking();
  const isContentReady = useContentReady(shouldTrackContentReady);

  useEffect(() => {
    if (isContentReady && shouldTrackContentReady) {
      const mainElement = document.querySelector('main#root');
      if (mainElement) {
        mainElement.classList.add('content-ready');
      }
    }
  }, [isContentReady, shouldTrackContentReady]);

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isNetworkConnectionSlow, setIsNetworkConnectionSlow] = useState(false);

  useEffect(() => {
    if (lms.isAvailable) {
      lms.setProgressMeasure(Number((+scorePercent / 100).toFixed(2)));
    }
  }, [scorePercent, sequenceId, lms]);

  useEffect(() => {
    interface NetworkInformationConnection extends EventTarget {
      downlink: number;
    }

    let connection: NetworkInformationConnection | undefined;
    let checkNetworkSpeed: (() => void) | undefined;

    if ('connection' in navigator) {
      connection =
        navigator.connection as unknown as NetworkInformationConnection;

      checkNetworkSpeed = () => {
        if (connection!.downlink < 1.35) {
          setIsNetworkConnectionSlow(true);
        } else {
          setIsNetworkConnectionSlow(false);
        }
      };

      checkNetworkSpeed();
      connection.addEventListener('change', checkNetworkSpeed);
    }

    const handlePopState = () => {
      const { player, setPlayer } = useGlobalMediaStore.getState();

      if (player) {
        try {
          player.pause();
        } catch {
          // Player may already be disposed
        }
      }

      setPlayer(undefined);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      if (connection && checkNetworkSpeed) {
        connection.removeEventListener('change', checkNetworkSpeed);
      }
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const checkCanScroll = useCallback(() => {
    if (!containerRef.current) {
      setCanScroll(false);
      setScrollCheckPending(false);
      return;
    }
    const { scrollTop, clientHeight, scrollHeight } = containerRef.current;
    setCanScroll(
      scrollTop + clientHeight < scrollHeight - SCROLL_OVERFLOW_THRESHOLD_PX
    );
    setScrollCheckPending(false);
  }, []);

  useEffect(() => {
    const handleStatusChange = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleStatusChange);
    window.addEventListener('offline', handleStatusChange);

    return () => {
      window.removeEventListener('online', handleStatusChange);
      window.removeEventListener('offline', handleStatusChange);
    };
  }, [isOnline]);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(checkCanScroll);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [activateSequence, checkCanScroll]);

  const sequence = items.find(
    i => i.kind === 'sequence' && i.id === sequenceId
  ) as SequenceDef;

  const { data, loading, error } =
    sequence && sequence.chapterSequenceId
      ? useSequenceDetailsQuery({
          variables: {
            learnerTrainingUuid: learnerTrainingId!,
            chapterSequenceId: sequence.chapterSequenceId
          }
        })
      : { data: null, loading: false, error: false };

  useEffect(() => {
    if (!data?.sequence) return;

    setRxdInteractions(
      data.sequence.sendRxdInteractions !== null
        ? data.sequence.sendRxdInteractions
        : true
    );
  }, [data?.sequence, setRxdInteractions]);

  // Re-check when content loads (e.g. layout settles after async render)
  useEffect(() => {
    if (!data?.sequence) return;

    checkCanScroll();
    const timer = setTimeout(checkCanScroll, 100);
    const timer2 = setTimeout(checkCanScroll, 500);
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
    };
  }, [data?.sequence?.id, checkCanScroll]);

  useLayoutEffect(() => {
    if (!sequence) return;

    clearBlocker();
    // Hold Next until overflow is re-measured (non-LMS only). Do not set
    // canScroll=true — that would flash the scroll hint on short slides.
    setCanScroll(false);
    setScrollCheckPending(true);
    // CAT-1281: LMS must never gate Next on scroll.
    setNeedsScrollDown(!lms.isAvailable);
    setPlayer(undefined);
    reset();
    activateSequence(sequence.id);
  }, [sequence]);

  const isChatBotTemplate = data?.sequence?.templateName === 'ChatBot';
  const showScrollHint = canScroll && !scrollCheckPending && !isChatBotTemplate;

  useEffect(() => {
    // Scroll arrow still uses showScrollHint; Next gating skips LMS (CAT-1281).
    setNeedsScrollDown(
      !lms.isAvailable && (scrollCheckPending || showScrollHint)
    );
  }, [scrollCheckPending, showScrollHint, setNeedsScrollDown, lms.isAvailable]);

  if (!sequence) return <NotFound />;

  // Animates the transition between sequences
  const transitions = useTransition(sequence, {
    from: { clipPath: 'circle(0% at 95% 95%)' },
    enter: newChapter
      ? () => async next => {
          await next({ clipPath: 'circle(70% at 95% 95%)' });
          await next({ clipPath: 'circle(140% at 95% 95%)' });
        }
      : { clipPath: 'circle(140% at 95% 95%)' },
    leave: { clipPath: 'circle(200% at 95% 95%)' },
    config: newChapter ? config.slow : config.molasses
  });

  if (!loading && !!error) return <NotFound btnTitle="Back to my courses" />;

  const addBackgroundColor =
    !!data?.sequence && data.sequence.templateName !== 'Images2BackgroundColor';

  return (
    <x.div
      w="100%"
      h="100%"
      display="grid"
      overflow="auto"
      onScroll={checkCanScroll}
      data-test-id="sequence-scroll-container"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={containerRef as any}
    >
      {isOnline && isNetworkConnectionSlow ? (
        <InternetOff isNetworkConnectionSlow />
      ) : null}
      {!isOnline ? <InternetOff /> : null}
      {transitions((props, item) => (
        <SequenceWrapper
          style={props}
          key={item.id}
          ref={childRef}
          setBackgroundColor={addBackgroundColor}
        >
          {item && <SequenceComponent sequence={item} />}
        </SequenceWrapper>
      ))}
      <ComboButton />
      <TenSecsBack />
      <ReplayMediaBtn />
      <SkipButtons />
      {showScrollHint && (
        <ScrollIndicator
          role="status"
          aria-live="polite"
          aria-label={t`Scroll for more content`}
        >
          <ScrollIndicatorMotion aria-hidden>
            {isDarkMode ? <ScrollDownDark /> : <ScrollDownLight />}
          </ScrollIndicatorMotion>
        </ScrollIndicator>
      )}
      <MediaCaptions />
    </x.div>
  );
};
